import type { NextPage } from 'next'

import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import Grid from '../../components/grid/Grid'
import CustomImage from '../../components/customImage/CustomImage'

import styles from '../../styles/pages/Downloads.module.scss'

import CustomMetaData from '../../components/general/customMetaData/CustomMetaData'

import { GenericPageLayoutProps } from '../../components/general/genericPageLayout/GenericPageLayout'

import { useGlobalContentContext } from '../../context/GlobalContentContext'

import { parseGenericPage } from '../../utils/parsers'
import { getAPI, createMarkup } from '../../utils/helpers'

import NewsPreviewItem, { NewsProps } from '../../components/news/News'
import { stripTags, replaceLast } from '../../utils/stringutils.js'
type Props = {
  downloads: any
} & GenericPageLayoutProps

const DownloadCategory = (props: Props) => {
  const { setData } = useGlobalContentContext()

  useEffect(() => {
    setData(props.globalData)
  }, [props.globalData, setData])

  const get_title = () => {
    if (props.downloads.length == 0) {
      return props.originalResponse.title
    }
    return replaceLast(
      props.originalResponse.title,
      '</p>',
      `: ${props.downloads[0].category.title}</p>`
    )
  }
  const category = props.downloads.length > 0 ? props.downloads[0].category : ''

  return (
    <div className={styles.container}>
      <CustomMetaData
        title={stripTags(props.meta_title, ' ').trim()}
        description={props.meta_description}
        image={props.meta_image}
      />

      <div className={styles.main}>
        <section className={styles.contentContainer}>
          <h1
            className={styles.title}
            dangerouslySetInnerHTML={createMarkup(get_title())}
          ></h1>

          <div className={styles.details}>
            <div className={styles.column1}>
              <Link href="/downloads">
                <a>
                  ALL
                  <br />
                  MEDIA DOWNLOADS
                </a>
              </Link>
            </div>
            <div className={styles.column2}>
              {category.title}
              <br />
              {props.downloads.length} PHOTOS
            </div>
            {category && (
              <div className={styles.column3}>
                <div className={styles.icon}>
                  <Image src="/download-icon.svg" layout="fill" alt="" />
                </div>
                <a
                  href={'/media/sakuudownloads/' + category.slug + '.zip'}
                  download
                >
                  DOWNLOAD ALL FILES
                </a>
              </div>
            )}
          </div>
        </section>

        <section className={styles.downloadsContainer}>
          <Grid columns={2}>
            {props.downloads.map((download: any, index: number) => (
              <div key={index}>
                {download.image && (
                  <div className={styles.imageContainer}>
                    <CustomImage
                      src={download}
                      layout="fill"
                      objectFit="cover"
                    />
                  </div>
                )}
                <div className={styles.downloadTitle}>
                  <span>{download.title}</span>
                  <a
                    href={download.file ? download.file : download.image}
                    download
                  >
                    <div className={styles.icon}>
                      <Image src="/download-icon.svg" layout="fill" alt="" />
                    </div>
                    DOWNLOAD
                  </a>
                </div>
              </div>
            ))}
          </Grid>
        </section>
      </div>
    </div>
  )
}

type ParamsType = {
  params: {
    slug: string
  }
}

export async function getStaticProps({ params }: ParamsType) {
  const page: GenericPageLayoutProps = await parseGenericPage(
    `${getAPI()}/downloadspage`
  )

  const downloadsRequest = await fetch(
    `${getAPI()}/downloads/?category=${params.slug}`
  )
  const downloads = await downloadsRequest.json()

  return {
    props: {
      originalResponse: page.originalResponse,
      globalData: page.globalData,
      downloads: downloads,
      title: page.title,
      meta_title: page.meta_title,
      meta_description: page.meta_description,
    },
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS,
  }
}

export async function getStaticPaths() {
  const res = await fetch(`${getAPI()}/download-categories/`)
  const categories = await res.json()

  const paths = categories.map((category: any) => ({
    params: { slug: category.slug },
  }))

  return {
    paths,
    fallback: false,
  }
}

export const NEXTJS_REVALIDATE_PAGE_IN_SECONDS = 10
export default DownloadCategory
